import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Popper, { type PopperProps } from '@mui/material/Popper';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STEP_COUNT_TOKEN, type TourStep } from './tourSteps.js';
import type { TourApi } from './useProductTour.js';

const REMEASURE_MS = [40, 220, 500, 900];
const SCROLL_TOP_OFFSET = 160;
const SPOTLIGHT_PADDING = 12;
const SPOTLIGHT_MARGIN = 2;
const SPOTLIGHT_RADIUS = 14;
const SCRIM_OPACITY = 0.74;
const ANCHORED_WIDTH = 344;
const CENTERED_WIDTH = 380;
const RAIL_ITEM_MAX_WIDTH = 120;
const RAIL_VIEWPORT_SHARE = 0.3;
const ARROW_SIZE = 12;
const NO_TARGET: TourTarget = { element: null, rect: null };
const POPPER_MODIFIERS = (arrow: HTMLElement | null): PopperProps['modifiers'] => [
  { name: 'offset', options: { offset: [0, 14] } },
  { name: 'flip', options: { fallbackPlacements: ['top', 'left', 'right'] } },
  { name: 'preventOverflow', options: { padding: 16 } },
  { name: 'arrow', options: { element: arrow, padding: 16 } },
];
// Popper writes transform and the main-axis side inline on the arrow, so the
// rotation goes on a pseudo-element and the cross-axis side on margins.
const ARROW_SX = {
  position: 'absolute',
  width: ARROW_SIZE,
  height: ARROW_SIZE,
  '&::before': {
    content: '""',
    display: 'block',
    width: '100%',
    height: '100%',
    bgcolor: 'background.paper',
    transform: 'rotate(45deg)',
  },
};
const ARROW_OFFSET = `${-ARROW_SIZE / 2}px`;
// Popper only positions the arrow along the main axis; the edge it sits against
// is ours to set, or it stays at the wrapper's top-left and hides under the card.
const ARROW_PLACEMENT_SX = {
  '&[data-popper-placement*="bottom"] .tour-arrow': { top: 0, marginTop: ARROW_OFFSET },
  '&[data-popper-placement*="top"] .tour-arrow': { bottom: 0, marginBottom: ARROW_OFFSET },
  '&[data-popper-placement*="right"] .tour-arrow': { left: 0, marginLeft: ARROW_OFFSET },
  '&[data-popper-placement*="left"] .tour-arrow': { right: 0, marginRight: ARROW_OFFSET },
};

interface TourTarget {
  element: HTMLElement | null;
  rect: DOMRect | null;
}

interface SpotlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ProductTourProps {
  tour: TourApi;
}

interface TourCardProps {
  tour: TourApi;
  width: number;
}

export function spotlightBox(rect: DOMRect, view: { width: number; height: number }): SpotlightBox {
  const top = Math.max(SPOTLIGHT_MARGIN, rect.top - SPOTLIGHT_PADDING);
  const left = Math.max(SPOTLIGHT_MARGIN, rect.left - SPOTLIGHT_PADDING);
  const right = Math.min(view.width - SPOTLIGHT_MARGIN, rect.right + SPOTLIGHT_PADDING);
  const bottom = Math.min(view.height - SPOTLIGHT_MARGIN, rect.bottom + SPOTLIGHT_PADDING);
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

function fillStepCount(text: string, total: number): string {
  return text.split(STEP_COUNT_TOKEN).join(String(total));
}

function scrollIntoFocus(element: HTMLElement): void {
  const previousMargin = element.style.scrollMarginTop;
  element.style.scrollMarginTop = `${SCROLL_TOP_OFFSET}px`;
  element.scrollIntoView({ block: 'start' });
  element.style.scrollMarginTop = previousMargin;
}

function useTourTarget(step: TourStep): TourTarget {
  const [target, setTarget] = useState<TourTarget>(NO_TARGET);
  const scrolled = useRef(false);

  const measure = useCallback(() => {
    if (step.target === null) {
      setTarget(NO_TARGET);
      return;
    }
    const element = document.querySelector<HTMLElement>(step.target);
    if (!element?.isConnected) {
      setTarget(NO_TARGET);
      return;
    }
    if (step.scroll && !scrolled.current) {
      scrolled.current = true;
      scrollIntoFocus(element);
    }
    const box = element.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) {
      setTarget(NO_TARGET);
      return;
    }
    setTarget({ element, rect: box });
  }, [step]);

  useEffect(() => {
    scrolled.current = false;
    measure();
    const timers = REMEASURE_MS.map((delay) => window.setTimeout(measure, delay));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      for (const timer of timers) clearTimeout(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // The timers above run out after 900ms; a list that loads later would leave
  // the ring around whatever was measured first, usually a spinner.
  useEffect(() => {
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    if (target.element !== null) observer.observe(target.element);
    return () => observer.disconnect();
  }, [measure, target.element]);

  return target;
}

function StepDots({ tour }: { tour: TourApi }): ReactNode {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {tour.steps.map((dotStep, position) => (
        <Box
          key={dotStep.key}
          component="button"
          type="button"
          aria-label={`Go to step ${position + 1}`}
          onClick={() => tour.goTo(position)}
          sx={{
            width: position === tour.index ? 22 : 7,
            height: 6,
            borderRadius: 3,
            border: 0,
            p: 0,
            cursor: 'pointer',
            bgcolor: position === tour.index ? 'primary.main' : 'background.muted',
            transition: 'width .12s',
          }}
        />
      ))}
    </Box>
  );
}

function TourCard({ tour, width }: TourCardProps): ReactNode {
  const { step } = tour;
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'relative',
        zIndex: 1,
        width,
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
        p: step.big ? 4 : 2.5,
        borderRadius: 3,
      }}
    >
      {step.big ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              background: (currentTheme) => currentTheme.gradients.primary,
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeOutlined sx={{ fontSize: 28 }} />
          </Box>
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: '0.06em', color: 'primary.main' }}
        >
          {step.eyebrow}
        </Typography>
        <StepDots tour={tour} />
      </Box>
      <Typography id="tour-title" variant={step.big ? 'h1' : 'h2'} sx={{ mb: 1 }}>
        {step.title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        {fillStepCount(step.body, tour.total)}
      </Typography>
      {step.video ? (
        <Box
          component="video"
          src={step.video.src}
          aria-label={step.video.label}
          autoPlay
          muted
          loop
          playsInline
          controls
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '55vh',
            mx: 'auto',
            border: '1px solid',
            borderColor: 'border.strong',
            borderRadius: 2,
            mb: 2,
          }}
        />
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {tour.isLast ? (
          <Box />
        ) : (
          <Button size="small" variant="text" onClick={tour.skip}>
            Skip tour
          </Button>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {tour.isFirst ? null : (
            <Button variant="text" onClick={tour.back}>
              Back
            </Button>
          )}
          <Button variant="gradient" onClick={tour.next}>
            {tour.nextLabel}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export function ProductTour({ tour }: ProductTourProps): ReactNode {
  const { element, rect } = useTourTarget(tour.step);
  const [arrow, setArrow] = useState<HTMLElement | null>(null);
  // A new array here rebuilds the popper on every scroll-driven re-measure.
  const modifiers = useMemo(() => POPPER_MODIFIERS(arrow), [arrow]);
  const anchored = element !== null && rect !== null;
  const width = tour.step.width ?? (anchored ? ANCHORED_WIDTH : CENTERED_WIDTH);
  const railItem =
    rect !== null &&
    rect.width < RAIL_ITEM_MAX_WIDTH &&
    rect.left < window.innerWidth * RAIL_VIEWPORT_SHARE;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      sx={{ position: 'fixed', inset: 0, zIndex: 'modal' }}
    >
      {anchored ? (
        <>
          <Box
            data-testid="tour-spotlight"
            sx={(currentTheme) => ({
              position: 'fixed',
              ...spotlightBox(rect, {
                width: window.innerWidth,
                height: window.innerHeight,
              }),
              borderRadius: `${SPOTLIGHT_RADIUS}px`,
              boxShadow: `0 0 0 9999px ${alpha(currentTheme.palette.common.black, SCRIM_OPACITY)}, inset 0 0 0 2px ${currentTheme.palette.primary.main}`,
              transition: 'top .32s, left .32s, width .32s, height .32s',
              pointerEvents: 'none',
            })}
          />
          <Popper
            open
            anchorEl={element}
            disablePortal
            placement={railItem ? 'right-start' : 'bottom'}
            modifiers={modifiers}
            sx={{ zIndex: 'modal', pointerEvents: 'auto', ...ARROW_PLACEMENT_SX }}
          >
            <Box sx={{ position: 'relative' }}>
              <Box className="tour-arrow" ref={setArrow} sx={ARROW_SX} />
              <TourCard tour={tour} width={width} />
            </Box>
          </Popper>
        </>
      ) : (
        <>
          <Backdrop
            open
            data-testid="tour-backdrop"
            onClick={tour.skip}
            sx={(currentTheme) => ({
              position: 'fixed',
              zIndex: 0,
              bgcolor: alpha(currentTheme.palette.common.black, SCRIM_OPACITY),
              pointerEvents: 'auto',
            })}
          />
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              pointerEvents: 'auto',
            }}
          >
            <TourCard tour={tour} width={width} />
          </Box>
        </>
      )}
    </Box>
  );
}
